import React, { useCallback, useEffect, useState } from 'react';
import { Col, Row, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import styles from './CreateDeliveryDetailedPage.module.less';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';
import Page from '../../components/Page/Page';
import TitleIcon from '../../assets/img/titleIcon.svg?react';
import Title from '../../components/Page/Title';
import useAnalytika from '../../utils/analytika/reporterAnalytika';
import {
  bookingChargeToCardAlertChangePaymentMethodClicked,
  bookingChargeToCardAlertDisplayed,
  bookingChargeToCardAlertTopUpClicked,
  negativeWalletBalanceAlertDisplayed,
  negativeWalletBalanceAlertTopUpClicked,
  requestDeliveryScreenOpen,
  zeroWalletBalanceAlertDisplayed,
  zeroWalletBalanceAlertTopUpClicked
} from '../../utils/analytika/events';
import Banner from '../../components/Banner/Banner';
import Button from '../../components/Button/Button';
import { getDisplayUserBalance } from '../../utils/numbers';
import { useHistory } from 'react-router-dom';
import { routes } from '../../utils/routerHelper';
import { PaymentType } from '../../api/models/payments';
import { ANALYTIKA_ACTIONS, ANALYTIKA_EVENTS_V2 } from '../../utils/analytika/enums';
import CreateDeliveryFormv2 from './CreateDeliveryFormv2';
import { ExclamationCircleOutlined, InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';
import publishAV2Event from '../../utils/analytika/reporterAnalytikaV2';
import { FeatureFlag } from '../../utils/galileo/featureFlag.enum';
import { SkeletonPageSet } from '../../components/WithSkeleton/WithSkeleton';
import { userSelector } from '../../redux/selectors/user';
import { AppDispatch } from '../../types/store';

const { useBreakpoint } = Grid;
const CreateDeliveryDetailedPage: React.FC = () => {
  const { t } = useTranslation();
  const { push: historyPush } = useHistory();

  const {
    userWalletConfig: { userBalance, isZeroWalletBalance },
    defaultPaymentMethodId,
    paymentMethods,
    loadCompleteFor: { blockingRequests: isBlockingRequestsComplete },
    secondaryPaymentMethodId,
    isWalletCreditUsed,
    userGalileoVars,
    merchantConfig,
    userData: { isEmailVerified }
  } = useSelector(userSelector);

  const {
    deliveries: { getIsFirstDeliveryDone },
    modals: { toggleIsEmailVerificationVisible, toggleIsAddAmountVisible }
  } = useDispatch<AppDispatch>();

  const [isFirstDeliveryScreenEnabled, setIsFirstDeliveryScreenEnabled] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);

  const {
    availablePaymentMethods: { isWalletEnabled },
    isVerifiedByOps
  } = merchantConfig;

  const {
    [FeatureFlag.IS_ACCOUNT_ACTIVATION_ENABLED]: isAccountActivationFlagEnabled,
    [FeatureFlag.IS_EMAIL_VERIFICATION_ENABLED]: isEmailVerificationEnabled
  } = userGalileoVars;

  const { fireEvent } = useAnalytika();

  const { md: isMdBreakpoint } = useBreakpoint();
  const { amount } = userBalance;

  const balanceDisplay = getDisplayUserBalance(userBalance, false);

  const defaultPaymentType = paymentMethods.find(method => method.id === defaultPaymentMethodId)?.paymentType;
  const isCardDefault = defaultPaymentType === PaymentType.CREDIT_CARD;

  const secondaryPayment = paymentMethods.find(method => method.id === secondaryPaymentMethodId);
  const secondaryPaymentType = secondaryPayment?.paymentType;
  const isCardSecondary = secondaryPaymentType === PaymentType.CREDIT_CARD;
  const secondaryPaymentCardDigits = isCardSecondary && secondaryPayment?.display.substring(15);

  const isNegativeBalance = Boolean(amount < 0);

  const isErrorNegativeBalanceCardBooking = isBlockingRequestsComplete && Boolean(isCardDefault && isNegativeBalance);

  const isWarningZeroBalanceNoPayment =
    isBlockingRequestsComplete &&
    Boolean(isWalletEnabled && paymentMethods && !paymentMethods.length && isZeroWalletBalance);

  const isWarningZeroBalanceCardSecondary =
    isBlockingRequestsComplete && Boolean(isWalletCreditUsed && isCardSecondary && isZeroWalletBalance);

  const isVerificationInProgressVisible = Boolean(isAccountActivationFlagEnabled) && !isVerifiedByOps;

  const isEmailVerificationBannerVisible = isEmailVerificationEnabled && !isEmailVerified;

  const isBookingBlocked =
    isErrorNegativeBalanceCardBooking || isWarningZeroBalanceNoPayment || isVerificationInProgressVisible;

  useEffect(() => {
    fireEvent(requestDeliveryScreenOpen({ active_deliveries_count: null }, ANALYTIKA_ACTIONS.PAGE_OPEN));
  }, []);

  useEffect(() => {
    if (isAccountActivationFlagEnabled) {
      getIsFirstDeliveryDone()
        .then((response: boolean) => {
          setIsFirstDeliveryScreenEnabled(!response);
          setIsPageLoading(false);
        })
        .catch(() => {
          setIsPageLoading(false);
        });
    } else {
      setIsPageLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isErrorNegativeBalanceCardBooking) {
      fireEvent(negativeWalletBalanceAlertDisplayed());
      publishAV2Event({ event_name: ANALYTIKA_EVENTS_V2.EXPR_ALERT_NEG_BALANCE, event_version: 2, value: `${amount}` });
    }
    if (isWarningZeroBalanceNoPayment) {
      fireEvent(zeroWalletBalanceAlertDisplayed());
      publishAV2Event({
        event_name: ANALYTIKA_EVENTS_V2.EXPR_ALERT_ZERO_BALANCE,
        event_version: 2,
        value: `${amount}`
      });
    }
    if (isWarningZeroBalanceCardSecondary) {
      fireEvent(bookingChargeToCardAlertDisplayed());
      publishAV2Event({
        event_name: ANALYTIKA_EVENTS_V2.EXPR_ALERT_CHARGE_TO_CARD,
        event_version: 6,
        value: `${amount}`
      });
    }
  }, [isErrorNegativeBalanceCardBooking, isWarningZeroBalanceNoPayment, isWarningZeroBalanceCardSecondary]);

  const openAmountModal = useCallback(() => {
    if (isErrorNegativeBalanceCardBooking) {
      fireEvent(negativeWalletBalanceAlertTopUpClicked());
      publishAV2Event({ event_name: ANALYTIKA_EVENTS_V2.EXPR_TOPUP_NEG_BALANCE, event_version: 2, value: `${amount}` });
    }
    if (isWarningZeroBalanceNoPayment) {
      fireEvent(zeroWalletBalanceAlertTopUpClicked());
      publishAV2Event({
        event_name: ANALYTIKA_EVENTS_V2.EXPR_TOPUP_ZERO_BALANCE,
        event_version: 2,
        value: `${amount}`
      });
    }
    if (isWarningZeroBalanceCardSecondary) {
      fireEvent(bookingChargeToCardAlertTopUpClicked());
      publishAV2Event({
        event_name: ANALYTIKA_EVENTS_V2.EXPR_TOPUP_CHARGE_TO_CARD,
        event_version: 2,
        value: `${amount}`
      });
    }
    toggleIsAddAmountVisible();
  }, [isErrorNegativeBalanceCardBooking, isWarningZeroBalanceNoPayment, isWarningZeroBalanceCardSecondary]);

  const openEmailVerificationModal = useCallback(() => {
    // TODO: instrumentation v2 should go here
    toggleIsEmailVerificationVisible();
  }, []);

  const pageTitle = isFirstDeliveryScreenEnabled ? t('deliveries.pageTitleFirstDelivery') : t('deliveries.pageTitle');

  return (
    <Page>
      {isPageLoading ? (
        <SkeletonPageSet />
      ) : (
        <Row className={styles.content}>
          <Col xs={24} md={24} xxl={24} className={classnames([styles.fullHeight, styles.formContainer])}>
            {isMdBreakpoint && <Title text={pageTitle} Icon={TitleIcon} />}
            <div className={styles.form}>
              {isEmailVerificationBannerVisible && (
                <Banner
                  message={t('deliveries.bannerEmailVerification.message')}
                  description={t('deliveries.bannerEmailVerification.description')}
                  action={
                    <Button
                      size={'middle'}
                      type={'default'}
                      onClick={openEmailVerificationModal}
                      block={!isMdBreakpoint}
                      data-testid="bannerEmailVerificationButton"
                    >
                      {t('deliveries.bannerEmailVerification.verifyButton')}
                    </Button>
                  }
                  type={'warning'}
                  showIcon
                  icon={<WarningOutlined />}
                />
              )}
              {isVerificationInProgressVisible && (
                <Banner
                  message={t('deliveries.bannerVerificationInProcess.message')}
                  description={t('deliveries.bannerVerificationInProcess.description')}
                  type={'warning'}
                  showIcon
                  icon={<WarningOutlined />}
                />
              )}
              {isErrorNegativeBalanceCardBooking && (
                <Banner
                  message={
                    <div className={styles.bannerTitleContent}>
                      <span>{t('deliveries.bannerNegativeBalance.message')}</span>
                      <Button
                        size={'middle'}
                        type={'default'}
                        onClick={openAmountModal}
                        data-testid="bannerNegativeBalanceButton"
                      >
                        {t('deliveries.bannerNegativeBalance.topupButton')}
                      </Button>
                    </div>
                  }
                  description={t('deliveries.bannerNegativeBalance.description', { amount: balanceDisplay })}
                  type={'error'}
                  showIcon
                  icon={<ExclamationCircleOutlined />}
                />
              )}
              {isWarningZeroBalanceNoPayment && (
                <Banner
                  message={
                    <div className={styles.bannerTitleContent}>
                      <span>{t('deliveries.bannerZeroBalance.message')}</span>
                      <Button
                        size={'middle'}
                        type={'default'}
                        onClick={openAmountModal}
                        data-testid="bannerZeroBalanceButton"
                      >
                        {t('deliveries.bannerZeroBalance.topupButton')}
                      </Button>
                    </div>
                  }
                  description={t('deliveries.bannerZeroBalance.description')}
                  type={'warning'}
                  showIcon
                  icon={<ExclamationCircleOutlined />}
                />
              )}
              {isWarningZeroBalanceCardSecondary && (
                <Banner
                  message={
                    <div className={styles.bannerTitleContent}>
                      <span>{t('deliveries.bannerZeroBalanceCardBooking.message')}</span>
                      <Button
                        size={'middle'}
                        type={'default'}
                        onClick={openAmountModal}
                        data-testid="bannerZeroBalanceCardBookingButton"
                      >
                        {t('deliveries.bannerZeroBalanceCardBooking.topupButton')}
                      </Button>
                    </div>
                  }
                  description={
                    <>
                      <div>
                        {t('deliveries.bannerZeroBalanceCardBooking.description_part_card', {
                          cardNumber: secondaryPaymentCardDigits
                        })}
                      </div>
                      <div>
                        <span>{t('deliveries.bannerZeroBalanceCardBooking.description_part_redirect_normal')}</span>
                        <a
                          data-testid="bannerZeroBalanceCardBookingLink"
                          onClick={() => {
                            fireEvent(bookingChargeToCardAlertChangePaymentMethodClicked());
                            historyPush(routes.payments);
                          }}
                        >
                          {t('deliveries.bannerZeroBalanceCardBooking.description_part_redirect_highligted')}
                        </a>
                      </div>
                    </>
                  }
                  type={'info'}
                  showIcon
                  icon={<InfoCircleOutlined />}
                />
              )}
              <CreateDeliveryFormv2 isBlocked={isBookingBlocked} isFirstDeliveryScreen={isFirstDeliveryScreenEnabled} />
            </div>
          </Col>
        </Row>
      )}
    </Page>
  );
};

export default CreateDeliveryDetailedPage;
